import { useState, type FormEvent } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Turnstile } from "./Turnstile";
import { formMessage, submitButtonLabel } from "./turnstile-ui";
import { useFormTurnstile, useLeadSubmit } from "./useLeadSubmit";

type Relationship = "Employee" | "Applicant" | "Patient" | "Other";

function splitName(full: string): { first_name: string; last_name: string } {
  const parts = full.trim().split(/\s+/);
  if (parts.length === 1) return { first_name: parts[0], last_name: parts[0] };
  return {
    first_name: parts.slice(0, -1).join(" "),
    last_name: parts[parts.length - 1],
  };
}

export function ConsumerRightsForm() {
  const { send, status, error } = useLeadSubmit();
  const { token, setToken, onToken, onError, verifyFailed, resetRef, formRef, nextToken } =
    useFormTurnstile("consumer_rights");
  const [isDataSubject, setIsDataSubject] = useState<"Yes" | "No">("Yes");
  const [relationship, setRelationship] = useState<Relationship>("Patient");

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!token) return;
    const fd = new FormData(e.currentTarget);
    const full = String(fd.get("full_name") || "").trim();
    const { first_name, last_name } = splitName(full);
    const sent = await send(
      {
        form_key: "consumer_rights",
        turnstile_token: token,
        is_data_subject: isDataSubject,
        first_name,
        last_name,
        email: String(fd.get("email") || "").trim(),
        phone: String(fd.get("mobile_phone") || "").trim(),
        home_phone: String(fd.get("home_phone") || "").trim() || undefined,
        dob: String(fd.get("dob") || "").trim() || undefined,
        address: String(fd.get("address") || "").trim(),
        relationship,
        relationship_other:
          relationship === "Other"
            ? String(fd.get("relationship_other") || "").trim() || undefined
            : undefined,
        requestor_name:
          isDataSubject === "No"
            ? String(fd.get("requestor_name") || "").trim() || undefined
            : undefined,
        requestor_relationship:
          isDataSubject === "No"
            ? String(fd.get("requestor_relationship") || "").trim() || undefined
            : undefined,
      },
      { nextToken, form: formRef.current },
    );
    // A failed send spent the token: get a new one for the next tap.
    if (!sent) {
      setToken(null);
      resetRef.current?.();
    }
  }

  if (status === "success") {
    return (
      <div className="rounded-lg border border-border bg-card p-6 text-card-foreground">
        <h3 className="text-xl font-semibold">Request received.</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          We will endeavor to respond within 45 calendar days. If we need
          additional information to process your request, we will contact you
          using the information you provided.
        </p>
      </div>
    );
  }

  return (
    <form ref={formRef} onSubmit={onSubmit} className="space-y-6">
      <fieldset className="space-y-2">
        <legend className="text-sm font-semibold text-foreground">
          Are you the data subject? *
        </legend>
        <div className="flex gap-6">
          {(["Yes", "No"] as const).map((v) => (
            <label key={v} className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="is_data_subject"
                value={v}
                checked={isDataSubject === v}
                onChange={() => setIsDataSubject(v)}
              />
              {v}
            </label>
          ))}
        </div>
      </fieldset>

      {isDataSubject === "No" && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 rounded-md border border-border bg-muted/40 p-4">
          <div className="space-y-1">
            <Label htmlFor="requestor_name">Your full name *</Label>
            <Input id="requestor_name" name="requestor_name" autoComplete="name" required maxLength={160} />
          </div>
          <div className="space-y-1">
            <Label htmlFor="requestor_relationship">
              Your relationship to the data subject
            </Label>
            <Input
              id="requestor_relationship"
              name="requestor_relationship"
              maxLength={200}
            />
          </div>
        </div>
      )}

      <div className="space-y-1">
        <Label htmlFor="full_name">Data Subject's Full Name *</Label>
        <Input id="full_name" name="full_name" autoComplete="name" required maxLength={160} />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="dob">Data Subject's Date of Birth</Label>
          <Input id="dob" name="dob" autoComplete="bday" type="date" maxLength={40} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="email">Data Subject's Email Address *</Label>
          <Input id="email" name="email" autoComplete="email" type="email" required maxLength={255} />
        </div>
      </div>

      <div className="space-y-1">
        <Label htmlFor="address">Data Subject's Home Address *</Label>
        <Input id="address" name="address" autoComplete="street-address" required maxLength={400} />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="mobile_phone">Data Subject's Mobile Phone *</Label>
          <Input id="mobile_phone" name="mobile_phone" autoComplete="tel" type="tel" required maxLength={32} />
        </div>
        <div className="space-y-1">
          <Label htmlFor="home_phone">Data Subject's Home Phone</Label>
          <Input id="home_phone" name="home_phone" autoComplete="home tel" type="tel" maxLength={32} />
        </div>
      </div>

      <fieldset className="space-y-2">
        <legend className="text-sm font-semibold text-foreground">
          Data Subject's Relationship to Us *
        </legend>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {(["Employee", "Applicant", "Patient", "Other"] as Relationship[]).map((v) => (
            <label key={v} className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="relationship"
                value={v}
                checked={relationship === v}
                onChange={() => setRelationship(v)}
              />
              {v}
            </label>
          ))}
        </div>
      </fieldset>

      {relationship === "Other" && (
        <div className="space-y-1">
          <Label htmlFor="relationship_other">Please specify *</Label>
          <Input
            id="relationship_other"
            name="relationship_other"
            required
            maxLength={200}
          />
        </div>
      )}

      <Turnstile
        appearance="interaction-only"
        resetRef={resetRef}
        onToken={onToken}
        onExpired={() => setToken(null)}
        onError={onError}
        action="consumer_rights"
      />

      {formMessage(error, verifyFailed, token) && (
        <p className="text-sm text-destructive">{formMessage(error, verifyFailed, token)}</p>
      )}

      <Button type="submit" disabled={!token || status === "submitting"} size="lg">
        {submitButtonLabel({
          token,
          submitting: status === "submitting",
          label: "Submit",
          submittingLabel: "Submitting…",
        })}
      </Button>
    </form>
  );
}